/* eslint-disable @typescript-eslint/ban-types */
import DebugService from '../debug.service';

const log = (...args) => DebugService.log('StateMachine', ...args);
// Simple Finite State Machine framework that leverages the power of typescript!

/**
 * Doing this for later when we might want special transition behaviour like push and pop.
 * For now, everything will be a 'normal' transition.
 */
export interface TransitionFuncResult<T = any> {
    // a destination state in the current state machine
    destStateName: string;

    // The updated (or even recreated) context object. This will replace the state machines context member.
    context: T;
}

export interface Transition<T = any> {
    // name of event, aka trigger
    eventName: string;

    // source state name
    sourceStateName: string;

    // dest state name. Omitted dest state name means a self transition.
    destStateName?: string;

    // To be used in place of destStateName. When defined, this function will
    // take any event arguments, do some logic, and return a string for the new state
    // The first arg is the internal context object, if it exists
    transitionFunc?: (context: T, ...args: any[]) => TransitionFuncResult<T>;

    // Any callbacks to run before the state transition but before the before state enter / exit events. Omitted means no actions
    beforeActions?: Function[];

    // Any callbacks to run after the state transition but before the after state enter / exit events
    afterActions?: Function[];
}

interface State {
    name: string; // name of state
    onBeforeEnterActions?: Function[]; // Any callbacks to run on before state enter.  Omitted means no actions
    onBeforeExitActions?: Function[]; // Any callbacks to run on before state exit. Omitted means no actions
    onAfterEnterActions?: Function[]; // Any callbacks to run on after state enter. Omitted means no actions
    onAfterExitActions?: Function[]; // Any callbacks to run on after state exit. Omitted means no actions
}

interface StateMachineOptions<T> {
    context?: T;
    startingState?: string; // set the starting state by name. handy for testing
    name?: string; // for identifying logs
}

// At the time of writing this is intended to be a pure state machine.
// State management is separated from any actions that need to be run. This makes it
// handy to retrofit into any existing classes that are suffering from state management
// issues. See the unit test for an example of how it works
// If you're doing something very complicated you may still want to wrap this up in another class
export class StateMachine<T = any> {
    private _states;
    private _currentState;
    private _transitions: Transition<T>[];
    private _context: T; // The public, arbitrary context useful for punching things in
    private _name: string;

    constructor(
        states: State[],
        transitions: Transition<T>[],
        options: StateMachineOptions<T> = {},
    ) {
        this._context = options.context;
        this._name = options.name;

        // always use the first state as the initial state
        if (!states.length) {
            throw new Error('You need at least one state!');
        }
        // I guess 0 transitions is doable but the state machine won't do much!

        let startingState = states[0];
        if (options.startingState) {
            // Find the state by name
            startingState = states.find((s) => s.name === options.startingState);
            if (!startingState) {
                throw new Error(
                    `Asked for starting state ${startingState} but it wasn't found in the states`,
                );
            }
        }

        this._currentState = startingState;
        this._states = states;
        transitions.forEach((t) => {
            if (t.destStateName && t.transitionFunc) {
                throw new Error(
                    `destStateName and transitionFunc both defined for a transition - pick one only!`,
                );
            }
        });
        this._transitions = transitions;
    }

    /**
     * Returns the mutable context. Probably don't mutate it unless you're sure
     * that's what you want!
     * @returns {T}
     */
    get context() {
        return this._context;
    }

    get currentStateName() {
        return this._currentState.name;
    }

    // Handle the event. Any defined actions will be called with all the
    // arguments of the handle event function minus the event name
    // Returns a promise.all of all the action results in case you need to wait for
    // any pesky async stuff.
    // If an event occurs that is not defined for the current state an exception will
    // be thrown.
    handleEvent(eventName: string, ...eventArgs): Promise<any> {
        const transition = this._lookupTransition(eventName, this.currentStateName);
        if (!transition) {
            throw new Error(
                `No transition defined for event ${eventName} in state ${this.currentStateName}`,
            );
        }
        // we have a transition - execute it!

        const actionResults = [];

        function executeActions(actions: Function[]) {
            if (actions) {
                actions.forEach((action) => {
                    const result = action(...eventArgs);
                    actionResults.push(result);
                });
            }
            // else, no actions to execute
        }

        executeActions(transition.beforeActions);

        let destStateName;
        if (transition.transitionFunc) {
            const transitionResult = transition.transitionFunc(this._context, ...eventArgs);
            this._context = transitionResult.context;
            destStateName = transitionResult.destStateName;
        } else {
            destStateName = transition.destStateName;
        }

        log(
            `SM: ${this._name} -> Event: ${eventName}, CurrentState: ${this.currentStateName}, DestState:${destStateName}`,
        );

        // Work out the new state. Undefined destStateName implies new state is equal to old state
        const newState: State = destStateName
            ? this._lookupStateByName(destStateName)
            : this._currentState;

        const oldState = this._currentState;
        const stateHasChanged = newState !== oldState;

        if (stateHasChanged) {
            // Do before exit actions
            executeActions(oldState.onBeforeExitActions);
            // Do before enter actions
            executeActions(newState.onBeforeEnterActions);
        }

        // Execute our state change. In the instant we are actually changing state things are
        // actually undefined. If javascript was not single threaded we would have to make sure that this
        // assignment is atomic.
        this._currentState = newState;

        executeActions(transition.afterActions);

        if (stateHasChanged) {
            // Do after exit actions
            executeActions(oldState.onAfterExitActions);
            // Do after enter actions
            executeActions(newState.onAfterEnterActions);
        }

        log(
            `SM: ${this._name} -> StateMachine handle event ${eventName}, ${oldState.name}, ${newState.name}`,
        );

        return Promise.all(actionResults);
    }

    // Not optimised - consider dict lookup if bottleneck forms
    _lookupStateByName(stateName: string) {
        const state = this._states.find((state) => state.name === stateName);
        if (!state) {
            throw new Error(`Could not find state with name ${stateName}`);
        }
        return state;
    }

    // Not optimised - consider dict lookup if bottleneck forms
    _lookupTransition(eventName: string, sourceStateName: string) {
        return this._transitions.find(
            (t) => t.sourceStateName === sourceStateName && t.eventName === eventName,
        );
    }

    /**
     * This should only be used for more convenient testing!
     * @param stateName
     */
    forceState(stateName: string) {
        this._currentState = this._lookupStateByName(stateName);
    }
}
